import CsiFormData from "csi/action/FormData";
import csiCard from "csi/card/_base";
import comm from "csi/comm";
import Aspera from "csi/comm/Aspera";
import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { ATTR_IMAGES, doImagePixelsExceedCount, ImageData, ImageDimensions, MIN_PROGRESSIVE_IMG_PIXEL_CT } from "csi/model/image";
import { getClass, TYPE_IMAGE } from "csi/reflection/reflection";
import { getClassDefaultPropertyView } from "csi/service/viewService";
import { submitWriter } from "csi/service/writerService";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, VALUE_TYPE } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { PATH_OP_PARENT, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import Writer from "csi/xml/Writer";
import { MinWidthHeightImageContainerOptions, PanningOptions, ProgressiveImageData } from "designSystem/components/box/TurntableImageBox";
import { BackgroundColorHandlerFunction, CIRCLE_DEFAULT_COLORS } from "designSystem/components/colorPicker/ColorPicker";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { saveAs } from "file-saver";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { createContext, KeyboardEventHandler, useContext } from "react";

export const FIT_TO_PERCENTAGE = 0;
export const FIT_TO_SCREEN = -1;
export const FIT_TO_WIDTH = -2;

export const INFO_PANEL_MIN_WIDTH = 304;
export const ZOOM_AMOUNT_INIT = 100;
export const ZOOM_DISABLE_ANIMATION = -1;
export const ZOOM_MIN_PERCENTAGE = 10;
export const ZOOM_MAX_PERCENTAGE = 400;
export const ZOOM_OPTION_FIT_TO_SCREEN_ID = "12";
export const ZOOM_OPTION_FIT_TO_WIDTH_ID = "13";
export const ZOOM_OPTION_GROUP_NUMBER = "number";
export const ZOOM_OPTION_GROUP_FIT = "fit";
export const WIDTH_OF_THUMBNAIL = 112;

export const ZOOM_SCROLL_OPTION_MAX_ID = "11";
export const ZOOM_SCROLL_OPTION_MIN_ID = "0";

export const MAX_RECENT_COLORS = 9;

const INIT_CONTAINER_DIM: ImageDimensions = { height: 0, width: 0 };

// amount to increment scale during zoom
const ZOOM_INCREMENT = 5;

/** Calculate new zoom level based on scroll delta */
function calculateNewZoomAmount(scalePercentage: number, deltaY: number): number {
	let zoomAmount = scalePercentage + deltaY;
	const mod = zoomAmount % ZOOM_INCREMENT;
	if (mod !== 0) {
		if (deltaY > 0) {
			zoomAmount -= mod;
		} else {
			zoomAmount += ZOOM_INCREMENT - mod;
		}
	}
	return Math.min(Math.max(zoomAmount, ZOOM_MIN_PERCENTAGE), ZOOM_MAX_PERCENTAGE);
};

export const zoomOptions: ComponentItem[] = [
	{ id: "1", name: "10%", group: ZOOM_OPTION_GROUP_NUMBER, value: 10 },
	{ id: "2", name: "25%", group: ZOOM_OPTION_GROUP_NUMBER, value: 25 },
	{ id: "3", name: "50%", group: ZOOM_OPTION_GROUP_NUMBER, value: 50 },
	{ id: "4", name: "75%", group: ZOOM_OPTION_GROUP_NUMBER, value: 75 },
	{ id: "5", name: "90%", group: ZOOM_OPTION_GROUP_NUMBER, value: 90 },
	{ id: "6", name: "100%", group: ZOOM_OPTION_GROUP_NUMBER, value: 100 },
	{ id: "7", name: "125%", group: ZOOM_OPTION_GROUP_NUMBER, value: 125 },
	{ id: "8", name: "150%", group: ZOOM_OPTION_GROUP_NUMBER, value: 150 },
	{ id: "9", name: "200%", group: ZOOM_OPTION_GROUP_NUMBER, value: 200 },
	{ id: "10", name: "300%", group: ZOOM_OPTION_GROUP_NUMBER, value: 300 },
	{ id: "11", name: "400%", group: ZOOM_OPTION_GROUP_NUMBER, value: 400 },
	{ id: ZOOM_OPTION_FIT_TO_SCREEN_ID, name: "FitToScreen", group: ZOOM_OPTION_GROUP_FIT, value: FIT_TO_SCREEN },
	{ id: ZOOM_OPTION_FIT_TO_WIDTH_ID, name: "FitToWidth", group: ZOOM_OPTION_GROUP_FIT, value: FIT_TO_WIDTH },
];

const checkeredBackground = "url(\"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAIAAAAlC+aJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAAEnQAABJ0Ad5mH3gAAAD/SURBVGhD7Y8xCgQxEMPu/z/NJ3avUeVG3jJgIQgpxODf+cjzPp8kq4lc3QCTrCZydQNMsprI1Q0wyWoiVzfAJKuJXN0Ak6wmcnUDTLKayNUNMMlqIlfvHxB/lTs1katkNRtgRK6S1WyAEblKVrMBRuQqWc0GGJGrZDUbYESuktVsgBG5SlazAUbkKlnN/QN4a+KeSlYTuboBJllN5OoGmGQ1kasbYJLVRK5ugElWE7m6ASZZTeTqBphkNZGrG2CS1USu3j8g/ip3aiJXyWo2wIhcJavZACNylaxmA4zIVbKaDTAiV8lqNsCIXCWr2QAjcpWsZgOMyFWymssHnPMHySqypI/MXCUAAAAASUVORK5CYII=\")";
const translucentColor = "rgba(240, 240, 240, 0.8)";

class LightboxImage {
	@observable.ref data: ImageData;
	readonly index: number;

	/** return copy of data */
	copy = (imageNode: C8Node, imageContainerNode: C8Node): ImageData => {
		return this.data.copyTo(imageNode, imageContainerNode);
	};

	/** update data after image was edited and replaced */
	@action replaceWith = (imageNode: C8Node): void => {
		this.data = this.data.replaceWith(imageNode);
	};

	constructor(data: ImageData, index: number) {
		makeObservable(this);
		this.data = data;
		this.index = index;
	}
}

/* This type refers to the cursor options */
export type CursorOptions = "default" | "grab" | "zoom-in";
/* This type refers to the confirmation dialog options */
type ConfirmDialogOptions = null | "closeLightbox" | "selectImage";
/* This type refers to the link/unlink all options */
type LinkOptions = "linkAll" | "unLinkAll";

export type LightboxChangeFunction = (oldImageUrl: cnl, newImageUrl: cnl, updatedNodeResponse: JsonResponse) => void;
export type LightboxEditCompleteFunction = (imageNode: C8Node, imageContainerNode: C8Node, replaced: boolean) => void;
export type LightboxEditFunction = (image: ImageData, onComplete: LightboxEditCompleteFunction) => void;

/** get the fit to width scale from a parent container and child's dimensions */
function getFitToWidthScaleFromDimensions(container: ImageDimensions, child: ImageDimensions): number {
	return Math.max(1, (container.width / child.width) * (child.height / container.height));
}

/** get new scale based on a parent and child container's image dimensions */
function getScaleFromDimensions(container: ImageDimensions, child: ImageDimensions): number {
	return Math.min(container.width / child.width, container.height / child.height);
}

interface IlinkUnlinkAllMode {
	/** indicate the "Link All" mode is activated */
	activateLinkAll: boolean;

	/** indicate the "Unlink All" mode is activated */
	activateUnLinkAll: boolean;
}

interface ISaveLabelParams {
	/** custom label for the save button (by using res mapping) */
	saveLabelRes: string;

	/** custom label parameters for the save button (similar to resParams ) */
	saveLabelResParams: string[];
}

export default class LightboxStore extends ReactionStore {
	private readonly _images: LightboxImage[] = observable.array<LightboxImage>([], { deep: false });
	private readonly _onBackgroundColorChange: BackgroundColorHandlerFunction;
	private readonly _onChange: LightboxChangeFunction;
	private readonly _onClose: VoidFunction;
	private readonly _onEdit: LightboxEditFunction;
	private readonly _cache = new WorkingCache();
	readonly allLinkedWhereUsed = observable.set<cnl>();
	readonly unlinkedWhereUsed = observable.set<cnl>();

	/** image dimensions are set when the image is loaded into lightbox */
	private _imageDimensions: ImageDimensions = INIT_CONTAINER_DIM;

	/** writer that tracks all attribute changes */
	private _lightboxWriter: Writer;

	/** thumbnail image index which user clicks on */
	private _selectedThumbnailImgIdx = 0;

	/** the scroll value is used to zoom the image in and out */
	private _scrollValue: number = 0;

	@observable private _backgroundColor: string;
	@observable private _dojoViewUserUpdated = false;
	@observable private _editModeIndividualImageLink = false;
	@observable private _editModeInfoPanel = false;
	@observable private _forceColorPickerUpdate: boolean;
	@observable private _imageAttributesCollapse = false;
	@observable.ref private _imageContainerDimensions: ImageDimensions = INIT_CONTAINER_DIM;
	@observable private _imageInformationCollapse = false;
	@observable private _imageLoaded: boolean = false;
	@observable private _imagesPerPage = 0;
	@observable private _imageWhereUsedCollapse = true;
	@observable private _index: number;
	@observable private _infoPanelCtr = 0;
	@observable private _lightBoxCloseConfirm: ConfirmDialogOptions = null;
	@observable private _linkAllModeImageWhereUsed = false;
	@observable private _openColorPicker = false;
	@observable private _openInfoPanel = false;
	@observable private _page = 0;
	@observable private _showInfoPanelCloseConfirm = false;
	@observable.ref private _recentCustomColors: string[];
	@observable private _viewSelected = FIT_TO_SCREEN;
	@observable private _zoomAmount = FIT_TO_SCREEN;
	@observable private _zoomDropDownSelected = ZOOM_OPTION_FIT_TO_SCREEN_ID;
	@observable private _zoomSelected = false;

	constructor(
		images: ImageData[],
		onBackgroundColorChange?: BackgroundColorHandlerFunction,
		onChange?: LightboxChangeFunction,
		onClose?: VoidFunction,
		onEdit?: LightboxEditFunction,
		startIndex?: number,
		backgroundColor?: string,
		recentCustomColors: string[] = [],
	) {
		super();
		makeObservable(this);

		// other initialization
		this._backgroundColor = backgroundColor || "#FFFFFF";
		this._onBackgroundColorChange = onBackgroundColorChange;
		this._onChange = onChange;
		this._onClose = onClose;
		this._onEdit = onEdit;
		this._resetWriter();

		const {
			selectImage,
			setRecentCustomColors,
			_images,
		} = this;

		images.forEach((data, i) => {
			_images.push(new LightboxImage(data, i));
		});

		setRecentCustomColors(recentCustomColors);
		selectImage(startIndex || 0);
	}

	/** download image to browser */
	downloadImage = (): void => {
		const { downloadUrl, imageType, lightboxSrc, name, url } = this.currentImage.data;

		if (this.isCurrentImageTurntable) {
			const postRequest = new PostRequest("SiteAdmin", "DownloadTurntableImages")
				.addParameter("URL", url);
			const args = { handleAs: "blob", OutputJSON: 0 };
			postRequest.post(args)
				.then((response) => {
					saveAs(response.data, `${name}`);
				});

			return;
		}

		if (Aspera.isEnabled()) {
			Aspera.downloadFile(downloadUrl);
		} else {
			const src = downloadUrl ? comm.getFileHref(downloadUrl) : lightboxSrc;
			saveAs(src, `${name}.${imageType}`);
		}
	};

	/** parameters to use to create image attributes view */
	get attributesViewParams(): Record<string, any> {
		const { cache, _lightboxWriter } = this;
		const source = {
			getAction: () => null,
			getCache: () => cache,
			getNode: () => null,
			getParent: () => null,
			getContext: () => null,
		};

		const formData = new CsiFormData(source);
		formData.linkTarget = "_blank";
		formData.m_bAllowSelections = false;
		formData.m_bAllowSelectionActions = false;
		formData.m_bEditable = this._editModeInfoPanel;

		const defView = getClassDefaultPropertyView(getClass(TYPE_IMAGE), {
			bRestrictComments: true,
			m_bSlim: true,
			m_formData: formData,
			m_nColumns: 1,
		});

		return {
			defView,
			useMuiTooltipStyling: true,
			writerForActions: _lightboxWriter,
		};
	}

	/** handles mouse scrolling zooming operation updates. */
	handleReactMouseScroll = (event: React.WheelEvent): void => {
		if (!this.imageLoaded) {
			return;
		}

		// get deltaY from wheel event
		const { deltaY } = event;

		// add deltaY to the scroll value
		this._scrollValue += deltaY;

		// destructure the scoll value
		const { _scrollValue } = this;

		// if the scroll is larger than the scroll value, scroll one time and reset the value
		if (Math.abs(_scrollValue) > ZOOM_INCREMENT) {
			this.zoomByScrollDelta(_scrollValue < 0 ? ZOOM_INCREMENT : -ZOOM_INCREMENT);
			this._scrollValue = 0;
		}
	};

	/** zoom by a certain scroll delta */
	@action zoomByScrollDelta = (deltaY: number): void => {
		const { scalePercentage } = this;
		const newZoom = calculateNewZoomAmount(scalePercentage, deltaY);
		this._zoomAmount = newZoom;
		this._zoomDropDownSelected = zoomOptions.find((x) => x.value === this.scalePercentage)?.id;
	};

	/** response to keyboard event */
	@action handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (e.code === "ArrowRight") {
			this.setNextImage();
		} else if (e.code === "ArrowLeft") {
			this.setPrevImage();
		} else if (e.code === "Escape") {
			this.onClose();
			e.stopPropagation();
		}
	};

	/** edit the selected image - if the image was edited and replaced, then update to reference the new image */
	@action onEditImage = (): void => {
		const { currentImage, numImages, _images, _onEdit } = this;
		const { copy, data, replaceWith } = currentImage;

		_onEdit?.(data, action((imageNode, imageContainerNode, replaced) => {
			if (replaced) {
				// replace existing data with new data
				replaceWith(imageNode);
			} else {
				// add new image
				_images.push(new LightboxImage(copy(imageNode, imageContainerNode), numImages));
				this._index = numImages;
			}

			this._imageLoaded = false;
		}));
	};

	/** setup for newly selected image. */
	@action selectImage = (byIndex: number): void => {
		this._zoomAmount = FIT_TO_SCREEN;
		this._zoomDropDownSelected = ZOOM_OPTION_FIT_TO_SCREEN_ID;
		if (this._index !== byIndex) {
			this._imageLoaded = false;
			this._openInfoPanel = false;
			this._index = byIndex;
			const { height, width } = this._images[this._index].data;
			this._imageDimensions = { height, width };
		}
		this.resetPage();
	};

	/** adjust zoom based on selected zoom option */
	@action selectZoomOption = (id: string, value: number): void => {
		this._zoomAmount = value;
		this._zoomDropDownSelected = id;
	};

	/** in progressive loading, set the large image as loaded */
	@action onImageLoaded = (): void => {
		this._imageLoaded = true;
	};

	/** The image's background color */
	get backgroundColor(): string {
		return this._backgroundColor;
	}

	/** Cache of the NodeDataStore */
	get cache(): WorkingCache {
		return this._cache;
	}

	/** return true if user allowed to edit image attributes */
	get canEditImageAttributes(): boolean {
		return this.showImageAttributes && this.currentImage.data.editableAttributes;
	}

	/** return true if user allowed to edit image file */
	get canEditImageFile(): boolean {
		return !!this._onEdit && this.currentImage.data.editableImage;
	}

	/** indicate whether "Where Image Is Used" section is in "Link All" mode */
	get linkAllModeImageWhereUsed(): boolean {
		return this._linkAllModeImageWhereUsed;
	}

	/** indicate whether "Where Image Is Used" section is in individual link/unlink edit mode (not link all/unlink all) */
	get editModeIndividualImageLink(): boolean {
		return this._editModeIndividualImageLink;
	}

	/** All the images */
	get images(): LightboxImage[] {
		return this._images;
	}

	/** Number of images per page in thumbnail container */
	get imagesPerPage(): number {
		return this._imagesPerPage;
	}

	/** Current image */
	get currentImage(): LightboxImage {
		return this._images[this._index];
	}

	/** Color picker force render flag */
	get forceColorPickerUpdate(): boolean {
		return this._forceColorPickerUpdate;
	}

	/** Backround color of the actual displayed image */
	get imageBackgroundColor(): string {
		switch (this._backgroundColor) {
			case "transparent":
				return checkeredBackground;
		}

		return this._backgroundColor;
	}

	/**
	 * In order to freeze the image size (not allow image resizing) in the percentage and mouse scrolling mode,
	 * we set the min-width and min-height of the image container.
	 */
	get minWidthHeightImageContainer(): MinWidthHeightImageContainerOptions | null {
		const { zoomAmount } = this;
		return (zoomAmount === FIT_TO_PERCENTAGE) ?
			{ minHeight: this.imageContainerDimensions.height, minWidth: this.imageContainerDimensions.width } : null;
	}

	/** return true if edit button should be shown which is true if any of the images in the lightbox are editable */
	get showEditButton(): boolean {
		return this._images.some((image) => !!image.data.editableImage);
	}

	/** Total number of pages in thumbnail scroll */
	get totalPages(): number {
		return Math.ceil(this.numImages / this._imagesPerPage);
	}

	/** Current page in thumbnail scroll */
	get page(): number {
		return this._page;
	}

	/** get images on the thumbnail page */
	@computed get imagesOnPage(): LightboxImage[] {
		return this._images.slice(this._page * this._imagesPerPage, (this._page + 1) * this._imagesPerPage);
	};

	/** get the index of the first image on the next page, if any */
	@computed get firstImageOnNextPage(): LightboxImage | undefined {
		return this._images[(this._page + 1) * this._imagesPerPage];
	}

	/** Index of the current image in the image array */
	get index(): number {
		return this._index;
	}

	/** Current zooming state of the image */
	get isZooming(): boolean {
		return this.derivedZoomAmountAsScale > 1;
	}

	/** in progressive image loading, get whether the large image is loaded */
	get imageLoaded(): boolean {
		return this._imageLoaded;
	}

	/** get current image preview src */
	get currentImagePreviewSrc(): string {
		return this.currentImage.data.previewSrc;
	}

	/** get current image dimensions */
	get currentImageDimensions(): ImageDimensions {
		const { height, width } = this.currentImage.data;
		return { height, width };
	}

	/** do the current image pixels exceed the min count for progressive loading */
	get doCurrentImagePixelsExceedCount(): boolean {
		const { height, width } = this.currentImageDimensions;
		return doImagePixelsExceedCount(height, width, MIN_PROGRESSIVE_IMG_PIXEL_CT);
	}

	/** is the current image a turntable image */
	get isCurrentImageTurntable(): boolean {
		return this.currentImage.data.isTurntable;
	}

	/** Current width for the image container */
	get imageContainerHeight(): number {
		return this.imageContainerDimensions.height;
	}

	/** Current width for the image container */
	get imageContainerWidth(): number {
		return this.imageContainerDimensions.width;
	}

	/** progressive image data */
	get progressiveImageData(): ProgressiveImageData | null {
		const {
			currentImageDimensions,
			currentImagePreviewSrc,
			doCurrentImagePixelsExceedCount,
			isCurrentImageTurntable,
		} = this;
		if (!isCurrentImageTurntable && doCurrentImagePixelsExceedCount) {
			return {
				previewImageSrc: currentImagePreviewSrc,
				...currentImageDimensions,
			};
		}
		return null;
	}

	/** Total number of the images */
	get numImages(): number {
		return this._images.length;
	}

	/** The image's list of recently used background colors */
	get recentCustomColors(): string[] {
		return this._recentCustomColors;
	}

	/** Labels of the image */
	get imageLabels(): string[] {
		return this.currentImage?.data.imageLabels || [];
	}

	/** Zoom factor, is the image zoom percentage chose by the user */
	get zoomAmount(): number {
		return this._zoomAmount;
	}

	/** derive the zoom amount as a scale that react-zoom-pan-pinch can use to centerView  */
	get derivedZoomAmountAsScale(): number {
		const { fitToWidthScale, isFitScreenToScreenMode, isFitScreenToWidthMode, scale, zoomAmount } = this;
		// if the zoom amount is greater than 0, then the scale is dynamic based on the container size
		if (zoomAmount > 0) {
			return (zoomAmount / 100) / scale;
		} else if (isFitScreenToScreenMode) {
			// in fit-to-screen mode, the scale can safely be kept at 1
			return 1;
		} else if (isFitScreenToWidthMode) {
			return fitToWidthScale;
		}
	}

	/** fit to width scale of image */
	get fitToWidthScale(): number {
		return getFitToWidthScaleFromDimensions(this.imageContainerDimensions, this.imageDimensions);
	}

	/** actual scale of image relative to its size */
	get scale(): number {
		return getScaleFromDimensions(this.imageContainerDimensions, this.imageDimensions);
	}

	/** actual scale percentage of image relative to its size */
	get scalePercentage(): number {
		const { fitToWidthScale, scale, zoomAmount } = this;

		if (zoomAmount === FIT_TO_SCREEN) {
			return Math.round(scale * 100);
		}

		if (zoomAmount === FIT_TO_WIDTH) {
			return Math.round(scale * fitToWidthScale * 100);
		}

		return zoomAmount;
	}

	/** indicates the dropdown zoom option is currently selected */
	get zoomDropDownSelected(): string {
		return this._zoomDropDownSelected;
	}

	/** indicates whether Zoom is currently selected */
	get zoomSelected(): boolean {
		return this._zoomSelected;
	}

	get imageContainerDimensions(): ImageDimensions {
		return this._imageContainerDimensions;
	}

	get imageDimensions(): ImageDimensions {
		return this._imageDimensions;
	}

	/** indicates whether Color Picker is currently selected */
	get openColorPicker(): boolean {
		return this._openColorPicker;
	}

	/** indicates whether Info Panel is currently selected */
	get openInfoPanel(): boolean {
		return this._openInfoPanel;
	}

	/** indicates whether the Info Panel close/saving diaglog should be shown */
	get showInfoPanelCloseConfirm(): boolean {
		return this._showInfoPanelCloseConfirm;
	}

	/** indicates whether confirm dialog is for lightbox closing or lightbox info panel closing or lightbox thumbnail clicking */
	get lightBoxCloseConfirm(): ConfirmDialogOptions {
		return this._lightBoxCloseConfirm;
	}

	/** indicates whether the Info Panel image custom attributes or link/unlink status has been updated */
	get infoPanelHasBeenUpdated(): boolean {
		return this.unlinkedWhereUsed.size > 0 || this._dojoViewUserUpdated;
	}

	/** indicates whether image information section is currently collapsed */
	get imageInformationCollapse(): boolean {
		return this._imageInformationCollapse;
	}

	/** indicates whether image attributes section is currently collapsed */
	get imageAttributesCollapse(): boolean {
		return this._imageAttributesCollapse;
	}

	/** indicates whether image where used section is currently collapsed */
	get imageWhereIsUsedCollapse(): boolean {
		return this._imageWhereUsedCollapse;
	}

	@computed get panningOptions(): PanningOptions {
		return { disabled: !this.isZooming };
	}

	@computed get cursor(): CursorOptions {
		/* in order to set the cursor style correctly, we need to handle the following cases:
			image zoom amount is less than zooming max percentage (200%), use 'zoom' cursor (allow user to perform zooming action)
		
			image zoom amount is at the zooming state (zoom amount is larger than the fit to screen zooming value) and
			less than or equal to the zooming max percentage, use 'pan' cursor (allow user to perform panning action)
		
			image zoom amount is equal to or more than the zooming max percentage and
			not at the zooming state, use 'default' cursor
			(come from image with larger zoom amount (image with small intrinsic size) than zooming max percentage,
			 not allow user to perform zooming action)
		*/

		if (this._zoomAmount >= ZOOM_MAX_PERCENTAGE && !this.isZooming) {
			return "default";
		} else if (this.isZooming) {
			return "grab";
		} else {
			return "zoom-in";
		}
	}


	/** indicates whether the image is displaying in fit to percentage or fit to scroll mode */
	get isFitPercentageScrollMode(): boolean {
		return (this.zoomAmount !== FIT_TO_SCREEN && this.zoomAmount !== FIT_TO_WIDTH);
	}

	/** indicates whether the image is displaying in fit to screen or fit to width mode */
	get isFitScreenToWidthMode(): boolean {
		return this.zoomAmount === FIT_TO_WIDTH;
	}

	/** indicates whether the image is displaying in fit to screen or fit to width mode */
	get isFitScreenToScreenMode(): boolean {
		return this.zoomAmount === FIT_TO_SCREEN;
	}

	/** return the NodeDataStore for image WhereUsed search */
	@computed get dataStore(): NodeDataStore {
		const search = new Search();
		const { containerUrl, url } = this._images[this._index].data;

		search.setLimitPath(url, pathCreate(PATH_OP_PARENT, ATTR_IMAGES));
		search.getQuery()
			.Node(xml.PROP_TYPE, xml.OP_NOT_EQUALS, "FinalInspectionSpecDSLineItem")
			.Node(xml.PROP_TYPE, xml.OP_NOT_EQUALS, "ImageFolder")
			.Node(xml.PROP_TYPE, xml.OP_NOT_EQUALS, "Comment")
			.Node(xml.PROP_TYPE, xml.OP_NOT_EQUALS, "CommentReply");

		search.getSortQuery()
			.SortByNode(xml.PROP_NAME);

		return new NodeDataStore(search, null, containerUrl);
	}

	/** check whether the image has attributes in its attribute card */
	@computed get imageHasAttributeSpecs(): boolean {
		const specs = csiCard.getAttributeCardSlimSpecs(TYPE_IMAGE);

		if (specs?.attributeSpecs.length > 0) {
			const otherSpecs = csiCard.getAttributeSpecsMinusName(specs.attributeSpecs);
			return otherSpecs?.length > 0;
		}

		return false;
	}

	/** get the total number of nodes where the image are used */
	get imageWhereUsedCount(): number {
		return this.dataStore?.totalCount ?? 0;
	}

	/** get the Link/Unlink All activation mode */
	get linkUnlinkAllMode(): IlinkUnlinkAllMode {
		let activateLinkAll;
		let activateUnLinkAll;
		const { editModeIndividualImageLink, imageWhereUsedCount, linkAllModeImageWhereUsed, unlinkedWhereUsed } = this;

		if (imageWhereUsedCount === 1) {
			activateLinkAll = false;
			activateUnLinkAll = false;
		} else if (editModeIndividualImageLink) {
			// the current parent image container node is not allowed to link/unlink
			const imageAllowedLinkUnlinkCount = imageWhereUsedCount - 1;
			if (unlinkedWhereUsed.size === imageAllowedLinkUnlinkCount) {
				activateLinkAll = true;
				activateUnLinkAll = false;
			} else if (unlinkedWhereUsed.size === 0) {
				activateLinkAll = false;
				activateUnLinkAll = true;
			} else {
				activateLinkAll = true;
				activateUnLinkAll = true;
			}
		} else if (linkAllModeImageWhereUsed) {
			activateLinkAll = true;
			activateUnLinkAll = false;
		} else {
			activateLinkAll = false;
			activateUnLinkAll = true;
		}

		return { activateLinkAll, activateUnLinkAll };
	}

	/** get the save button label and label parameters (res and resParams) for info panel */
	get imageInfoPanelSaveLabelParams(): ISaveLabelParams {
		let saveLabelRes;
		let saveLabelResParams;
		const { imageWhereUsedCount, unlinkedWhereUsed } = this;

		if (imageWhereUsedCount === 1 || (imageWhereUsedCount - unlinkedWhereUsed.size === 1)) {
			saveLabelRes = "SaveWithCount";
			saveLabelResParams = ["1"];
		} else if (unlinkedWhereUsed.size === 0) {
			saveLabelRes = "SaveForAll";
			saveLabelResParams = [imageWhereUsedCount.toString()];
		} else {
			saveLabelRes = "SaveForSelected";
			saveLabelResParams = [(imageWhereUsedCount - unlinkedWhereUsed.size).toString()];
		}

		return { saveLabelRes, saveLabelResParams };
	}

	/** light box current view */
	get viewSelected(): number {
		return this._viewSelected;
	}

	/** indicates whether the image is displaying in fit to screen or fit to width mode */
	get isFitScreenWidthMode(): boolean {
		return (this._viewSelected === FIT_TO_SCREEN || this._viewSelected === FIT_TO_WIDTH);
	}

	/** indicates whether information panel is in edit mode */
	get editModeInfoPanel(): boolean {
		return this._editModeInfoPanel;
	}

	/** return true if image attributes should be shown for image */
	get showImageAttributes(): boolean {
		return this.currentImage.data.showAttributes && this.imageHasAttributeSpecs;
	}

	/** return true if panel indicating where image is used should be shown for image */
	get showImageWhereUsed(): boolean {
		return this.currentImage.data.showWhereUsed && this.imageHasAttributeSpecs;
	}

	/** return localized and sorted image labels for current image */
	getLocalizedImageLabels = (loc: ILocaleProvider): string[] => {
		return this.imageLabels.map((label) => loc.enumResource(label))
			.sort();
	};

	/** reset the thumbnail container page based off of the image index */
	resetPage(): void {
		this.setPage(Math.floor(this._index / this._imagesPerPage));
	}

	/** closes the color picker, but only if it's already open */
	@action closeColorPicker = (): void => {
		this.setOpenColorPicker(false);
		this.addRecentColor(this._backgroundColor);
		this._onBackgroundColorChange?.(this._backgroundColor, this._recentCustomColors);
	};

	/** add a recently used background color to the list of recent colors */
	@action addRecentColor = (color: string, isFinal: boolean = true): void => {
		const colorsList = isFinal ?
			this._recentCustomColors.slice(0, MAX_RECENT_COLORS) :
			this._recentCustomColors.slice(1, MAX_RECENT_COLORS + 1);

		const recentColor = color.substring(0, 1) === "#" ? color.toUpperCase() : color;

		if (isFinal) {
			// don't add color to the list if it's a default one.
			if (CIRCLE_DEFAULT_COLORS.includes(recentColor)) {
				return;
			}

			// don't add color to the list if it's already in it.
			// (skipping the first one because it represents the currently selected color while the lightbox is open.)
			if (this._recentCustomColors.find((existingColor, index) => (!!index && recentColor === existingColor))) {
				return;
			}
		}

		const recentCustomColors = [recentColor, ...colorsList.slice(0, MAX_RECENT_COLORS + 1)];
		this.setRecentCustomColors(recentCustomColors);
	};

	/** opens the color picker when the user clicks the corresponding menu option */
	@action colorPickerMenuClickHandler = (): void => {
		this.setOpenColorPicker(!this._openColorPicker);
	};

	/** track the background color change */
	@action onBackgroundColorChangeComplete = (color: string): void => {
		this._backgroundColor = color;
		this.addRecentColor(color, false);
	};

	/** call dialog close callback */
	onClose = (): void => {
		if (this.infoPanelHasBeenUpdated) {
			this._lightBoxCloseConfirm = "closeLightbox";
			this._showInfoPanelCloseConfirm = true;
		} else {
			this._onClose?.();
		}
	};

	/** cancel changes to image attributes */
	@action cancelChanges = (): void => {
		if (this.infoPanelHasBeenUpdated) {
			this._resetWriter();
			this.unlinkedWhereUsed.clear();
			this._dojoViewUserUpdated = false;
			this._editModeIndividualImageLink = false;
			this._linkAllModeImageWhereUsed = false;
			++this._infoPanelCtr;
		}

		this.toggleEditModeInfoPanel();
	};

	/** confirm discarding any updates in the info panel and close it */
	@action discardChanges = (): void => {
		if (this.infoPanelHasBeenUpdated) {
			this._showInfoPanelCloseConfirm = false;
			this.unlinkedWhereUsed.clear();
			this._dojoViewUserUpdated = false;
			this._editModeIndividualImageLink = false;
			this._linkAllModeImageWhereUsed = false;
			//come from clicking the close button of the lightbox or clicking the thumbnail image
			if (this._lightBoxCloseConfirm === "selectImage") {
				this.selectImage(this._selectedThumbnailImgIdx);
			} else if (this._lightBoxCloseConfirm === "closeLightbox") {
				this.onClose?.();
			} else if (this._lightBoxCloseConfirm === null) {
				this._openInfoPanel = !this._openInfoPanel;
			}

			this._lightBoxCloseConfirm = null;
			this._selectedThumbnailImgIdx = 0;
		}
	};

	/** set Info Panel close/saving confirm diaglog to be closed */
	@action onCancelDiscardChanges = (): void => {
		this._showInfoPanelCloseConfirm = false;
	};

	/** Image node URL */
	get imageNodeUrl(): cnl {
		return this.currentImage.data.url;
	}

	/** counter used to refresh the DojoView content */
	get infoPanelCtr(): number {
		return this._infoPanelCtr;
	}

	/** save changes to image attributes for all linked nodes */
	@action saveChanges = async (): Promise<boolean> => {
		const { cache, currentImage, dataStore, imageNodeUrl, unlinkedWhereUsed, _lightboxWriter } = this;
		const unlinkedUrls = [...unlinkedWhereUsed];
		const writer = new Writer();
		let newImageUrl: cnl;

		if (unlinkedUrls.length) {
			// create new image and update references for all linked urls
			newImageUrl = comm.createUrl();

			writer.copyNode(newImageUrl, imageNodeUrl);
			writer.callMethod("SiteAdmin", "ReplaceImage")
				.addParameter("URL", VALUE_TYPE.CNL, imageNodeUrl)
				.addParameter("NewImageURL", VALUE_TYPE.CNL, newImageUrl)
				.addParameter("Unlink", VALUE_TYPE.CNL_LIST, unlinkedUrls);

			// swap imageNodeUrl for newImageUrl for attribute changes
			_lightboxWriter.replaceAttributeValue(imageNodeUrl, newImageUrl);
		}

		writer.addOperationsFrom(_lightboxWriter);

		const response = await submitWriter(writer);

		runInAction(() => {
			cache.cacheResponse(response);
			this._resetWriter();
			this.unlinkedWhereUsed.clear();
			this.allLinkedWhereUsed.clear();

			// update data on current image
			if (newImageUrl) {
				currentImage.replaceWith(cache.get(newImageUrl));
			}

			// remove unlinked nodes from data store
			for (const url of unlinkedUrls) {
				dataStore.remove(url);
			}

			// notify of change
			this._onChange(imageNodeUrl, newImageUrl, response);
		});

		this.toggleEditModeInfoPanel();
		return false;
	};

	/** set dimensions of the turntable image box container */
	@action setImageContainerDimensions = (width: number, height: number): void => {
		this._imageContainerDimensions = { height, width };
	};

	/** set the recently used background colors */
	@action private setRecentCustomColors = (colors: string[] = []): void => {
		while (colors.length < MAX_RECENT_COLORS + 1) {
			colors.push("#FFFFFF");
		}
		this._recentCustomColors = colors;
	};

	/** set the force color picker component rendering flag */
	@action setForceColorPickerUpdate = (): void => {
		this._forceColorPickerUpdate = !this._forceColorPickerUpdate;
	};

	/** set the index of the next image */
	@action setNextImage = (): void => {
		this.selectImage((this._index + 1) % this.numImages);
	};

	/** set the index of the previous image */
	@action setPrevImage = (): void => {
		this.selectImage((this._index - 1 + this.numImages) % this.numImages);
	};

	/** set the current page of the thumbnail scroll */
	@action setPage = (page: number): void => {
		this._page = page;
	};

	/** set the number of imager per page based off of the window width */
	@action setImagesPerPage = (width: number): void => {
		const widthOfAvailableSpace = width - 60; // width of the thumbnail container minus the width of the pagination arrows
		this._imagesPerPage = Math.floor(widthOfAvailableSpace / WIDTH_OF_THUMBNAIL);
		this.resetPage();
	};

	/** track the Color Picker state */
	@action setOpenColorPicker = (doOpen: boolean): void => {
		this._openColorPicker = doOpen;
	};

	/** track the Info Panel state */
	@action toggleInfoPanel = (): void => {
		if (this.infoPanelHasBeenUpdated && this._openInfoPanel) {
			this._lightBoxCloseConfirm = null;
			this._showInfoPanelCloseConfirm = true;
		} else {
			this._openInfoPanel = !this._openInfoPanel;
		}
	};

	/** toggle the image information card collapse/expand */
	@action toggleCollapsedImageInfo = (): void => {
		this._imageInformationCollapse = !this._imageInformationCollapse;
	};

	/** toggle the image attributes card collapse/expand */
	@action toggleCollapsedImageAttr = (): void => {
		this._imageAttributesCollapse = !this._imageAttributesCollapse;
	};

	/** toggle the image where used card collapse/expand */
	@action toggleCollapsedImageWhereUsed = (): void => {
		this._imageWhereUsedCollapse = !this._imageWhereUsedCollapse;
	};

	/** toggle the "where image is used" section link all/unlink all */
	@action toggleLinkUnlinkAllImageWhereUsed = (action: LinkOptions): void => {
		this._editModeIndividualImageLink = false;
		if (action === "unLinkAll") {
			this._linkAllModeImageWhereUsed = true;
			this.unlinkedWhereUsed.clear();

			if (this.allLinkedWhereUsed.size === 0) {
				const imageRefNodeUrls = this.dataStore.urls;
				let i = 1; // starting at the second element because the first one is the image container node
				for (; i < this.dataStore.totalCount; i++) {
					this.allLinkedWhereUsed.add(imageRefNodeUrls[i]);
				}
			}

			this.allLinkedWhereUsed.forEach((value) => {
				this.unlinkedWhereUsed.add(value);
			});
		} else if (action === "linkAll") {
			this._linkAllModeImageWhereUsed = false;
			this.unlinkedWhereUsed.clear();
		}
	};

	/** toggle the image link/delink */
	@action toggleWhereUsedLink = (nodeUrl: cnl): void => {
		this._editModeIndividualImageLink = true;
		const { unlinkedWhereUsed } = this;
		if (unlinkedWhereUsed.has(nodeUrl)) {
			unlinkedWhereUsed.delete(nodeUrl);
		} else {
			unlinkedWhereUsed.add(nodeUrl);
		}
	};

	/** toggle edit mode of the information panel */
	@action toggleEditModeInfoPanel = (): void => {
		this._editModeInfoPanel = !this._editModeInfoPanel;

		// update counter so that Dojo view will be refreshed
		++this._infoPanelCtr;
	};

	/** set the dojoview custom fields has been updated */
	@action private setDojoViewUserUpdated = (hasChanged: boolean): void => {
		this._dojoViewUserUpdated = hasChanged;
	};

	/** initialize writer and indicate it should operate in report mode so that any changes are not persistent */
	private _resetWriter(): void {
		const w = this._lightboxWriter = new Writer();
		w.setReport();
		w.resetAfterExecute = false;
		w.onChanged = this.setDojoViewUserUpdated;
	}
}

export const LightboxStoreContext = createContext<LightboxStore>(undefined);
export const useLightboxStore = (): LightboxStore => useContext(LightboxStoreContext);